import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { AVAILABLE_DAYS } from '../conventionDays';

const SLOT_DURATION_MIN = 120; // default 2-hour blocks

const advanceTime = (timeStr, minutes) => {
  const [h, m] = timeStr.split(':').map(Number);
  const total = h * 60 + m + minutes;
  const nh = Math.floor(total / 60) % 24;
  const nm = total % 60;
  return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
};

const slotDurationMinutes = (start, end) => {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let diff = (eh * 60 + em) - (sh * 60 + sm);
  if (diff <= 0) diff += 24 * 60;
  return diff;
};

const emptyForm = {
  department_id: '',
  selected_day: '',
  start_time: '09:00',
  end_time: '11:00',
  max_volunteers: 1,
  notes: '',
  double_time: false,
  on_call: false
};

function AdminTimeslotManager() {
  const [timeslots, setTimeslots] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [filterDeptId, setFilterDeptId] = useState('');

  useEffect(() => {
    fetchTimeslots();
    fetchDepartments();
  }, []);

  const fetchTimeslots = async () => {
    try {
      // Use the admin endpoint (uncached) instead of the public one,
      // so navigating between tabs always shows fresh data after edits.
      const res = await axios.get(`${API_URL}/timeslots/admin/data`);
      setTimeslots(res.data.timeslots);
      if (res.data.departments) setDepartments(res.data.departments);
    } catch (err) {
      console.error('Error fetching timeslots:', err);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await axios.get(`${API_URL}/departments`);
      setDepartments(res.data);
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  const convertToAPI = (data) => {
    const baseDate = data.selected_day;
    let endDate = baseDate;
    if (data.end_time <= data.start_time) {
      const next = new Date(baseDate);
      next.setDate(next.getDate() + 1);
      endDate = next.toISOString().split('T')[0];
    }
    return {
      department_id: parseInt(data.department_id),
      start_time: `${baseDate}T${data.start_time}:00`,
      end_time: `${endDate}T${data.end_time}:00`,
      max_volunteers: data.max_volunteers,
      notes: data.notes.trim() || null,
      double_time: !!data.double_time,
      on_call: !!data.on_call
    };
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      let newValue;
      if (type === 'checkbox') newValue = checked;
      else if (name === 'max_volunteers') newValue = parseInt(value);
      else newValue = value;
      const updated = { ...prev, [name]: newValue };
      if (name === 'start_time') {
        updated.end_time = advanceTime(value, SLOT_DURATION_MIN);
      }
      return updated;
    });
  };

  const validate = () => {
    if (!formData.department_id || !formData.selected_day) {
      setHasAttemptedSubmit(true);
      setMessage({ text: 'Department and date are required', type: 'error' });
      return false;
    }
    if (formData.start_time === formData.end_time) {
      setMessage({ text: 'Start and end times cannot be the same', type: 'error' });
      return false;
    }
    return true;
  };

  const handleCreate = async (e, addNext = false) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/timeslots`, convertToAPI(formData));
      setTimeslots(prev => [...prev, res.data]);
      setMessage({ text: 'Timeslot created!', type: 'success' });
      setTimeout(() => setMessage(''), 2500);

      if (addNext) {
        const dur = slotDurationMinutes(formData.start_time, formData.end_time);
        const newStart = advanceTime(formData.end_time, 0);
        setFormData(prev => ({
          ...prev,
          start_time: newStart,
          end_time: advanceTime(newStart, dur)
        }));
      } else {
        setFormData(emptyForm);
        setHasAttemptedSubmit(false);
      }
    } catch (err) {
      console.error(err);
      setMessage({ text: 'Error creating timeslot', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (slot) => {
    const s = new Date(slot.start_time);
    const e = new Date(slot.end_time);
    setEditingId(slot.id);
    setFormData({
      department_id: String(slot.department_id),
      selected_day: slot.start_time.slice(0, 10),
      start_time: slot.start_time.slice(11, 16),
      end_time: slot.end_time.slice(11, 16),
      max_volunteers: slot.max_volunteers,
      notes: slot.notes || '',
      double_time: !!slot.double_time,
      on_call: !!slot.on_call
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setHasAttemptedSubmit(false);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await axios.put(`${API_URL}/timeslots/${editingId}`, convertToAPI(formData));
      setTimeslots(prev => prev.map(s => s.id === editingId ? res.data : s));
      setMessage({ text: 'Timeslot updated!', type: 'success' });
      setEditingId(null);
      setFormData(emptyForm);
      setTimeout(() => setMessage(''), 2500);
    } catch (err) {
      console.error(err);
      setMessage({ text: 'Error updating timeslot', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (slot) => {
    const count = slot.current_signups || 0;
    const message = count > 0
      ? `This shift has ${count} active volunteer signup${count !== 1 ? 's' : ''}. ` +
        `Deleting it will remove ${count > 1 ? 'them all' : 'them'} permanently. Are you sure?`
      : 'Delete this timeslot?';
    if (!window.confirm(message)) return;
    try {
      await axios.delete(`${API_URL}/timeslots/${slot.id}`);
      setTimeslots(prev => prev.filter(s => s.id !== slot.id));
      setMessage({ text: 'Deleted', type: 'success' });
      setTimeout(() => setMessage(''), 2000);
    } catch (err) {
      setMessage({ text: 'Delete failed', type: 'error' });
    }
  };

  const handleDuplicate = async (slot) => {
    try {
      const res = await axios.post(`${API_URL}/timeslots/${slot.id}/duplicate`);
      setTimeslots(prev => [...prev, res.data]);
      setMessage({ text: 'Duplicated', type: 'success' });
      setTimeout(() => setMessage(''), 2000);
    } catch (err) {
      setMessage({ text: 'Duplicate failed', type: 'error' });
    }
  };

  const getDeptName = (id) => departments.find(d => d.id === id)?.name || 'Unknown';

  const fmtTime = (str) =>
    new Date(str).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

  const fmtDate = (str) =>
    new Date(str).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  // Apply department filter before grouping.
  const visibleSlots = filterDeptId
    ? timeslots.filter(s => s.department_id === parseInt(filterDeptId))
    : timeslots;

  // Group slots by date — slot.start_time is "YYYY-MM-DDTHH:MM:SS" (Eastern,
  // since admins enter times in convention-local time). Just take the date
  // prefix; no Date parsing means no timezone conversion can mangle it.
  const slotsByDay = visibleSlots.reduce((acc, slot) => {
    const day = (slot.start_time || '').slice(0, 10);
    if (!day) return acc;
    if (!acc[day]) acc[day] = [];
    acc[day].push(slot);
    return acc;
  }, {});
  const sortedDays = Object.keys(slotsByDay).sort();

  return (
    <div className="tsm-container">

      {/* ── FORM ── */}
      <div className="tsm-form-card">
        <h3 className="tsm-form-title">
          {editingId ? 'Edit Timeslot' : 'Create Timeslot'}
        </h3>

        {message && (
          <div className={`message tsm-toast ${message.type}`}>{message.text}</div>
        )}

        <form onSubmit={editingId ? handleSaveEdit : handleCreate}>

          <div className="form-group">
            <label>Department</label>
            <select name="department_id" value={formData.department_id} onChange={handleChange}>
              <option value="">Select department...</option>
              {[...departments].sort((a, b) => a.name.localeCompare(b.name)).map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Date</label>
            <div className="tsm-day-selector">
              {AVAILABLE_DAYS.map(day => (
                <button
                  key={day.date}
                  type="button"
                  className={`tsm-day-btn ${formData.selected_day === day.date ? 'active' : ''}`}
                  onClick={() => setFormData(p => ({ ...p, selected_day: day.date }))}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>

          <div className="tsm-time-row">
            <div className="form-group">
              <label>Start Time</label>
              <input type="time" name="start_time" value={formData.start_time} onChange={handleChange} />
            </div>
            <div className="tsm-time-sep">→</div>
            <div className="form-group">
              <label>End Time</label>
              <input type="time" name="end_time" value={formData.end_time} onChange={handleChange} />
            </div>
            <div className="form-group tsm-max-field">
              <label>Max Volunteers</label>
              <input type="number" name="max_volunteers" min="1" value={formData.max_volunteers} onChange={handleChange} />
            </div>
          </div>

          <div className="form-group">
            <label>Notes <span className="tsm-optional">(optional)</span></label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Any additional instructions or details for volunteers…"
              rows={3}
              className="tsm-notes-input"
            />
          </div>

          <div className="form-group tsm-double-time-row">
            <label className="tsm-checkbox-label">
              <input
                type="checkbox"
                name="double_time"
                checked={!!formData.double_time}
                onChange={handleChange}
              />
              <span>Double-time shift (counts as 2× hours)</span>
            </label>
          </div>

          <div className="form-group tsm-double-time-row">
            <label className="tsm-checkbox-label">
              <input
                type="checkbox"
                name="on_call"
                checked={!!formData.on_call}
                onChange={handleChange}
              />
              <span>On-call shift (visible to on-call viewer departments)</span>
            </label>
          </div>

          {hasAttemptedSubmit && (!formData.department_id || !formData.selected_day) && (
            <span className="alert-required">⚠ Department and date are required</span>
          )}

          <div className="tsm-btn-row">
            {editingId ? (
              <>
                <button type="submit" className="tsm-btn tsm-btn-save" disabled={loading}>
                  {loading ? 'Saving…' : 'Save Changes'}
                </button>
                <button type="button" className="tsm-btn tsm-btn-cancel" onClick={handleCancelEdit}>
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button type="submit" className="tsm-btn tsm-btn-create" disabled={loading}>
                  {loading ? 'Creating…' : 'Create'}
                </button>
                <button
                  type="button"
                  className="tsm-btn tsm-btn-next"
                  disabled={loading}
                  onClick={(e) => handleCreate(e, true)}
                >
                  Save & Add Next
                </button>
              </>
            )}
          </div>

        </form>
      </div>

      {/* ── SLOT LIST ── */}
      <div className="tsm-list">
        <div className="tsm-list-header">
          <h3 className="tsm-list-title">
            Timeslots
            <span className="tsm-list-count">{visibleSlots.length}</span>
          </h3>

          <div className="tsm-list-filter">
            <label htmlFor="tsm-dept-filter">Department</label>
            <select
              id="tsm-dept-filter"
              value={filterDeptId}
              onChange={(e) => setFilterDeptId(e.target.value)}
            >
              <option value="">All departments</option>
              {[...departments].sort((a, b) => a.name.localeCompare(b.name)).map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            {filterDeptId && (
              <button
                type="button"
                className="tsm-list-filter-clear"
                onClick={() => setFilterDeptId('')}
                aria-label="Clear filter"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {sortedDays.length === 0 && (
          <div className="no-data">
            {filterDeptId ? 'No timeslots for this department.' : 'No timeslots yet.'}
          </div>
        )}

        {sortedDays.map(day => (
          <div key={day} className="tsm-day-group">
            <div className="tsm-day-header">{fmtDate(day + 'T12:00:00')}</div>
            {slotsByDay[day]
              .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
              .map(slot => (
                <div key={slot.id} className={`tsm-slot-row ${editingId === slot.id ? 'editing' : ''}`}>
                  <div className="tsm-slot-dept">
                    {getDeptName(slot.department_id)}
                    {slot.double_time && <span className="tsm-double-badge">2×</span>}
                  </div>
                  <div className="tsm-slot-notes">{slot.notes || ''}</div>
                  <div className="tsm-slot-time">
                    {fmtTime(slot.start_time)} – {fmtTime(slot.end_time)}
                  </div>
                  <div className="tsm-slot-cap">
                    <span className={slot.current_signups >= slot.max_volunteers ? 'cap-full' : ''}>
                      {slot.current_signups}/{slot.max_volunteers}
                    </span>
                  </div>
                  <div className="tsm-slot-actions">
                    <button className="tsm-action-btn tsm-edit" onClick={() => handleEdit(slot)}>Edit</button>
                    <button className="tsm-action-btn tsm-dupe" onClick={() => handleDuplicate(slot)}>Dupe</button>
                    <button className="tsm-action-btn tsm-del" onClick={() => handleDelete(slot)}>Del</button>
                  </div>
                </div>
              ))}
          </div>
        ))}
      </div>

    </div>
  );
}

export default AdminTimeslotManager;
